import type { ProjectTask, TaskDependencyEdge } from '../types';
import type { TaskDateState } from './taskSchedule';
import { stripTime } from './taskSchedule';
import { getDependencyEdges } from './taskDependencies';
import { formatGanttTaskIdForLog } from './ganttTaskIds';

/**
 * Timeline / dependency schedule debugging (browser console).
 *
 * - **Development (`npm start`)**: off by default. Enable with `localStorage.setItem('ganttDebug','1')`.
 * - **Production build**: `localStorage.setItem('ganttDebug','1')` or `window.__GANTT_DEBUG__ = true`,
 *   or set `REACT_APP_GANTT_DEBUG=1` at build time.
 *
 * Helpers (after hub loads): `window.__ganttDebugOn()` / `window.__ganttDebugOff()`
 */

const LS_KEY = 'ganttDebug';

export function isGanttScheduleDebugEnabled(): boolean {
  if (typeof window === 'undefined') return false;
  try {
    const w = window as unknown as { __GANTT_DEBUG__?: boolean };
    if (w.__GANTT_DEBUG__ === true) return true;
    if (process.env.REACT_APP_GANTT_DEBUG === '1') return true;
    const v = localStorage.getItem(LS_KEY);
    return v === '1' || v === 'true';
  } catch {
    return false;
  }
}

export function ganttScheduleDebugLog(...args: unknown[]): void {
  if (!isGanttScheduleDebugEnabled()) return;
  // eslint-disable-next-line no-console
  console.log('[GanttSchedule]', ...args);
}

export function ganttScheduleDebugWarn(...args: unknown[]): void {
  if (!isGanttScheduleDebugEnabled()) return;
  // eslint-disable-next-line no-console
  console.warn('[GanttSchedule]', ...args);
}

export function ganttScheduleDebugGroup(label: string, fn: () => void): void {
  if (!isGanttScheduleDebugEnabled()) {
    fn();
    return;
  }
  // eslint-disable-next-line no-console
  console.groupCollapsed(`[GanttSchedule] ${label}`);
  try {
    fn();
  } finally {
    // eslint-disable-next-line no-console
    console.groupEnd();
  }
}

export function fmtDay(d: Date): string {
  return stripTime(d).toISOString().slice(0, 10);
}

export function fmtTaskRange(id: string, start: Date, end: Date): string {
  return `${id.slice(0, 12)}… ${fmtDay(start)} → ${fmtDay(end)}`;
}

export function formatEdgesBrief(edges: TaskDependencyEdge[]): string {
  if (!edges.length) return '(none)';
  return edges
    .map(
      (e) =>
        `${e.predecessorId.slice(0, 8)}…${e.type} lag=${e.lagBusinessDays ?? 0}`,
    )
    .join('; ');
}

/** Snapshot of dates for a set of task ids (from scheduling graph). */
export function snapshotTaskDates(
  tasks: ProjectTask[],
  ids: Iterable<string>,
): Record<string, { start: string; end: string }> {
  const byId = new Map(tasks.filter((t) => t.id).map((t) => [t.id!, t]));
  const out: Record<string, { start: string; end: string }> = {};
  for (const id of Array.from(ids)) {
    const t = byId.get(id);
    if (!t) continue;
    out[id] = { start: fmtDay(new Date(t.startDate)), end: fmtDay(new Date(t.endDate)) };
  }
  return out;
}

export function logPersistScheduleSnapshot(
  source: string,
  successorId: string,
  schedulingTasks: ProjectTask[],
  dates: Map<string, TaskDateState>,
  changes: { id: string; startDate: Date; endDate: Date }[],
  lagPatches: Map<string, TaskDependencyEdge[]>,
): void {
  if (!isGanttScheduleDebugEnabled()) return;
  const changedIds = new Set(changes.map((c) => c.id));
  const lagIds = new Set(lagPatches.keys());
  const all = new Set<string>([
    ...Array.from(changedIds),
    ...Array.from(lagIds),
    successorId,
  ]);
  ganttScheduleDebugGroup(`persist · ${source} · successor ${formatGanttTaskIdForLog(successorId)}`, () => {
    ganttScheduleDebugLog('date changes count', changes.length);
    for (const c of changes) {
      ganttScheduleDebugLog('  move', fmtTaskRange(c.id, c.startDate, c.endDate));
    }
    if (lagPatches.size) {
      for (const [tid, edges] of Array.from(lagPatches.entries())) {
        const before = schedulingTasks.find((t) => t.id === tid);
        const prev = before ? formatEdgesBrief(getDependencyEdges(before)) : '?';
        ganttScheduleDebugLog('  lag patch', tid.slice(0, 12) + '…', { before: prev, after: formatEdgesBrief(edges) });
      }
    } else {
      ganttScheduleDebugLog('lag patches: none');
    }
    ganttScheduleDebugLog('enforced dates map size', dates.size, 'union touched ids', all.size);
    const snap = snapshotTaskDates(schedulingTasks, all);
    ganttScheduleDebugLog('stored tasks (subset, before persist)', snap);
    const after: Record<string, { start: string; end: string }> = {};
    for (const id of Array.from(all)) {
      const d = dates.get(id);
      if (d) after[id] = { start: fmtDay(d.startDate), end: fmtDay(d.endDate) };
    }
    ganttScheduleDebugLog('enforced state (subset)', after);
  });
}

/** Call once from the app shell (e.g. ProjectHub) so DevTools can toggle without remembering keys. */
export function installGanttScheduleDebugApi(): void {
  if (typeof window === 'undefined') return;
  const w = window as unknown as {
    __ganttDebugOn?: () => void;
    __ganttDebugOff?: () => void;
    __GANTT_DEBUG__?: boolean;
  };
  w.__ganttDebugOn = () => {
    try {
      localStorage.setItem(LS_KEY, '1');
    } catch {
      /* ignore */
    }
    w.__GANTT_DEBUG__ = true;
    ganttScheduleDebugLog('enabled (tab flag + localStorage; dev is already on unless ganttDebug=0)');
  };
  w.__ganttDebugOff = () => {
    try {
      localStorage.setItem(LS_KEY, '0');
    } catch {
      /* ignore */
    }
    w.__GANTT_DEBUG__ = false;
    // eslint-disable-next-line no-console
    console.info('[GanttSchedule] verbose debug silenced (set ganttDebug=0; reload to fully match)');
  };
}
